// Thin client for the VoiceAssistant edge functions. Every call injects organization_id
// and unwraps the { data, error } envelope from supabase.functions.invoke.
import { supabase } from "@/lib/supabase";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_ANON_KEY } from "@/config/supabase";
import { voiceEngineErrorMessage } from "./engine-errors";
import type {
  VoiceAssistantRetellAccount,
  VoiceAssistant,
  VoiceAssistantKnowledgeBase,
} from "@/voice-assistants/types/database.types";

export const KB_LAST_SOURCE_CODE = "kb_last_source";

export class VoiceApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "VoiceApiError";
    this.code = code;
  }
}

export async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // On a non-2xx, supabase-js does NOT parse the body — it sets `data` to null
    // and `error.message` to the generic "Edge Function returned a non-2xx status
    // code". The real message (e.g. "Invalid hostname provided") lives in the
    // Response carried by FunctionsHttpError.context — read it so the user sees
    // exactly what the network returned.
    let msg = (data as { error?: string } | null)?.error ?? "";
    let code = (data as { code?: string } | null)?.code;
    const ctx = (error as { context?: unknown }).context;
    if (!msg && ctx instanceof Response) {
      try {
        const parsed = (await ctx.clone().json()) as { error?: string; message?: string; code?: string };
        msg = parsed?.error || parsed?.message || "";
        code = code ?? parsed?.code;
      } catch {
        try {
          msg = (await ctx.clone().text()) || "";
        } catch {
          /* body unavailable — fall through to error.message */
        }
      }
    }
    throw new VoiceApiError(voiceEngineErrorMessage(msg || error.message), code);
  }
  // Only a truthy `error` means failure — some endpoints always include the key
  // (e.g. `{ ok: true, error: null }`), and `"error" in data` would misread that
  // as an error and throw `new Error(null)` (message "null").
  const dataErr = (data as { error?: unknown } | null)?.error;
  if (dataErr) {
    throw new Error(voiceEngineErrorMessage(String(dataErr)));
  }
  return data as T;
}

/**
 * One line of the text simulation: what the caller typed, what the agent said,
 * or — role "event" — a function the agent called and how it ended up.
 */
export interface SimulatedTurn {
  role: "user" | "agent" | "event";
  content: string;
  /** event: the function's name in the assistant's configuration. */
  tool?: string;
  /** event: executed (ran) · skipped (shown, not run) · error · terminal (call ends). */
  status?: "executed" | "skipped" | "error" | "terminal";
  /** event: arguments and response, for the expandable detail. */
  detail?: string;
}

/** A function the agent has armed, as the simulator sees it. */
export interface SimulatedTool {
  name: string;
  type: string;
}

/** A Retell knowledge base source (document / text / url). */
export interface KbSourceRemote {
  source_id?: string;
  type?: string;
  source_type?: string;
  title?: string;
  url?: string;
  filename?: string;
  file_name?: string;
  file_size?: number;
}

/** A Retell knowledge base (workspace-level, linkable to assistants). */
export interface KbSummary {
  id: string;
  name: string;
  status: string | null;
  sources: KbSourceRemote[];
}

export interface RetellVoice {
  voice_id: string;
  voice_name?: string;
  provider?: string;
  accent?: string;
  gender?: string;
  age?: string;
  preview_audio_url?: string;
}

/** A community voice returned by Retell's /search-community-voice. */
export interface CommunityVoice {
  provider_voice_id: string;
  name: string;
  description?: string;
  /** ElevenLabs only — required when adding the voice. */
  public_user_id?: string;
}

export interface RetellVersion {
  version: number;
  description: string | null;
  is_published: boolean;
  modified_at: number | null;
  base_version: number | null;
}

export interface AgentImportPreview {
  agent_id: string;
  name: string;
  voice_id: string | null;
  language: string | null;
  languages?: string[] | null;
  llm_model: string | null;
  prompt_excerpt: string;
  is_supported: boolean;
  unsupported_reason: string | null;
  already_imported: boolean;
  /** The preview came from an exported file rather than a live agent id. */
  from_file?: boolean;
  /** Importing will create a new agent on the voice engine. */
  creates_engine_agent?: boolean;
  /** False when the file names a cloned voice this workspace's account does not hold. */
  voice_transfers?: boolean;
  tool_count?: number;
  knowledge_base_count?: number;
}

/** Retell-side config of a past agent version (VoiceAssistant-only fields not included). */
export interface AgentVersionConfig {
  voice_id: string | null;
  language: string | null;
  languages?: string[] | null;
  voice_speed: number | null;
  voice_temperature: number | null;
  start_speaker: string | null;
  llm_model: string | null;
  temperature: number | null;
  system_prompt: string | null;
  begin_message: string | null;
  advanced: Record<string, unknown>;
}

export const voiceAssistantsApi = {
  // ── Retell account ──
  validateRetellKey: (organization_id: string, api_key: string) =>
    invoke<{ valid: boolean; error?: string }>("voice-assistants-validate-retell-api-key", {
      organization_id,
      api_key,
    }),

  listRetellAccounts: (organization_id: string) =>
    invoke<{ accounts: VoiceAssistantRetellAccount[] }>("voice-assistants-manage-retell-accounts", {
      organization_id,
      action: "list",
    }),

  saveRetellAccount: (
    organization_id: string,
    api_key: string,
    label = "Principal",
    is_default = true,
  ) =>
    invoke<{ account: { id: string; key_hint: string } }>(
      "voice-assistants-manage-retell-accounts",
      { organization_id, action: "save", api_key, label, is_default },
    ),

  // ── Voices ──
  listVoices: (organization_id: string) =>
    invoke<{ voices: RetellVoice[] }>("voice-assistants-list-voices", { organization_id }),

  /** Search Retell community voices (provider ∈ elevenlabs|cartesia|minimax|fish_audio). */
  searchCommunityVoices: (organization_id: string, search_query: string, voice_provider?: string) =>
    invoke<{ voices: CommunityVoice[] }>("voice-assistants-community-voices", {
      organization_id,
      action: "search",
      search_query,
      voice_provider,
    }),

  /** Add a found community voice to the library → returns the usable RetellVoice. */
  addCommunityVoice: (
    organization_id: string,
    provider_voice_id: string,
    voice_name: string,
    voice_provider?: string,
    public_user_id?: string,
  ) =>
    invoke<{ voice: RetellVoice }>("voice-assistants-community-voices", {
      organization_id,
      action: "add",
      provider_voice_id,
      voice_name,
      voice_provider,
      public_user_id,
    }),

  // ── Assistants ──
  createAssistant: (organization_id: string, payload: Record<string, unknown>) =>
    invoke<{ assistant: VoiceAssistant }>("voice-assistants-create-assistant", {
      organization_id,
      ...payload,
    }),

  getAssistant: (organization_id: string, id: string, include_remote = false) =>
    invoke<{ assistant: VoiceAssistant; remote: unknown }>("voice-assistants-get-assistant", {
      organization_id,
      id,
      include_remote,
    }),

  updateAssistant: (organization_id: string, id: string, patch: Record<string, unknown>) =>
    invoke<{ assistant: VoiceAssistant }>("voice-assistants-update-assistant", {
      organization_id,
      id,
      patch,
    }),

  duplicateAssistant: (organization_id: string, id: string) =>
    invoke<{ assistant: VoiceAssistant }>("voice-assistants-duplicate-assistant", { organization_id, id }),

  deleteAssistant: (organization_id: string, id: string) =>
    invoke<{ ok: boolean }>("voice-assistants-delete-assistant", { organization_id, id }),

  // ── Knowledge base ──
  getKnowledgeBase: (organization_id: string, assistant_id: string) =>
    invoke<{ knowledge_base: VoiceAssistantKnowledgeBase | null }>(
      "voice-assistants-manage-knowledge-base",
      { organization_id, assistant_id, action: "get" },
    ),

  saveKnowledgeBase: (
    organization_id: string,
    assistant_id: string,
    sources: Array<{ title: string; content: string }>,
    config?: { top_k?: number; filter_score?: number; name?: string; instruction?: string },
  ) =>
    invoke<{ knowledge_base: VoiceAssistantKnowledgeBase }>("voice-assistants-manage-knowledge-base", {
      organization_id,
      assistant_id,
      action: "save",
      sources,
      name: config?.name,
      top_k: config?.top_k,
      filter_score: config?.filter_score,
      instruction: config?.instruction,
    }),

  deleteKnowledgeBase: (organization_id: string, assistant_id: string) =>
    invoke<{ ok: boolean }>("voice-assistants-manage-knowledge-base", {
      organization_id,
      assistant_id,
      action: "delete",
    }),

  // ── Workspace knowledge bases (Retell-native, linked via the LLM) ──
  listKnowledgeBases: (organization_id: string) =>
    invoke<{ knowledge_bases: KbSummary[] }>("voice-assistants-knowledge-bases", {
      organization_id,
      action: "list",
    }),

  getKnowledgeBaseById: (organization_id: string, knowledge_base_id: string) =>
    invoke<{ knowledge_base: KbSummary }>("voice-assistants-knowledge-bases", {
      organization_id,
      knowledge_base_id,
      action: "get",
    }),

  deleteKbSource: (organization_id: string, knowledge_base_id: string, source_id: string) =>
    invoke<{ ok: boolean }>("voice-assistants-knowledge-bases", {
      organization_id,
      knowledge_base_id,
      source_id,
      action: "delete_source",
    }),

  downloadKbSource: async (organization_id: string, knowledge_base_id: string, source_id: string) => {
    const data = await invoke<unknown>("voice-assistants-knowledge-bases", {
      organization_id,
      knowledge_base_id,
      source_id,
      action: "download_source",
    });
    if (!(data instanceof Blob)) throw new VoiceApiError("No se pudo descargar el archivo");
    return data;
  },

  getKbUsage: (organization_id: string, knowledge_base_id: string) =>
    invoke<{ assistants: Array<{ id: string; name: string }> }>("voice-assistants-knowledge-bases", {
      organization_id,
      knowledge_base_id,
      action: "usage",
    }),

  deleteWorkspaceKb: (organization_id: string, knowledge_base_id: string) =>
    invoke<{ ok: boolean; unlinked?: string[] }>("voice-assistants-knowledge-bases", {
      organization_id,
      knowledge_base_id,
      action: "delete",
    }),

  listLinkedKbs: (organization_id: string, assistant_id: string) =>
    invoke<{ knowledge_bases: KbSummary[]; top_k: number; filter_score: number; instruction: string }>(
      "voice-assistants-knowledge-bases",
      { organization_id, assistant_id, action: "linked" },
    ),

  linkKb: (organization_id: string, assistant_id: string, knowledge_base_id: string) =>
    invoke<{ ok: boolean }>("voice-assistants-knowledge-bases", {
      organization_id,
      assistant_id,
      knowledge_base_id,
      action: "link",
    }),

  unlinkKb: (organization_id: string, assistant_id: string, knowledge_base_id: string) =>
    invoke<{ ok: boolean }>("voice-assistants-knowledge-bases", {
      organization_id,
      assistant_id,
      knowledge_base_id,
      action: "unlink",
    }),

  setKbConfig: (
    organization_id: string,
    assistant_id: string,
    top_k: number,
    filter_score: number,
    instruction?: string,
  ) =>
    invoke<{ ok: boolean }>("voice-assistants-knowledge-bases", {
      organization_id,
      assistant_id,
      action: "config",
      top_k,
      filter_score,
      instruction,
    }),

  // Create a new Retell KB from texts / urls / an optional file (multipart).
  async createKnowledgeBase(
    organization_id: string,
    payload: { name: string; texts?: Array<{ title: string; text: string }>; urls?: string[]; file?: File },
  ): Promise<{ knowledge_base: { id: string } }> {
    const fd = new FormData();
    fd.append("organization_id", organization_id);
    fd.append("action", "create");
    fd.append("name", payload.name);
    if (payload.texts?.length) fd.append("texts", JSON.stringify(payload.texts));
    if (payload.urls?.length) fd.append("urls", JSON.stringify(payload.urls));
    if (payload.file) fd.append("file", payload.file, payload.file.name);
    const { data, error } = await supabase.functions.invoke("voice-assistants-knowledge-bases", { body: fd });
    if (error) throw new Error(voiceEngineErrorMessage((data as { error?: string } | null)?.error || error.message));
    if (data && typeof data === "object" && "error" in data) {
      throw new Error(voiceEngineErrorMessage((data as { error: string }).error));
    }
    return data as { knowledge_base: { id: string } };
  },

  // Append texts / urls / a file to an existing Retell KB (multipart).
  async addKbSources(
    organization_id: string,
    knowledge_base_id: string,
    payload: { texts?: Array<{ title: string; text: string }>; urls?: string[]; file?: File },
  ): Promise<{ knowledge_base: KbSummary }> {
    const fd = new FormData();
    fd.append("organization_id", organization_id);
    fd.append("action", "add_sources");
    fd.append("knowledge_base_id", knowledge_base_id);
    if (payload.texts?.length) fd.append("texts", JSON.stringify(payload.texts));
    if (payload.urls?.length) fd.append("urls", JSON.stringify(payload.urls));
    if (payload.file) fd.append("file", payload.file, payload.file.name);
    const { data, error } = await supabase.functions.invoke("voice-assistants-knowledge-bases", { body: fd });
    if (error) throw new Error(voiceEngineErrorMessage((data as { error?: string } | null)?.error || error.message));
    if (data && typeof data === "object" && "error" in data) {
      throw new Error(voiceEngineErrorMessage((data as { error: string }).error));
    }
    return data as { knowledge_base: KbSummary };
  },

  // ── Test (web call + text simulation) ──
  createWebCall: (organization_id: string, assistant_id: string) =>
    invoke<{ access_token: string; call_id: string }>("voice-assistants-create-web-call", {
      organization_id,
      assistant_id,
    }),

  /** Opening turn of the text simulation (empty when the caller speaks first). */
  startChatTest: (organization_id: string, assistant_id: string) =>
    invoke<{
      turns: SimulatedTurn[];
      ended: boolean;
      tools: SimulatedTool[];
      missing_variables: string[];
    }>("voice-assistants-chat-test", { organization_id, assistant_id, action: "start" }),

  /**
   * The simulation is stateless: post the whole transcript, get back what the
   * agent says and does next. `execute_tools` decides whether a custom function
   * is really fired at its endpoint or only reported.
   */
  sendChatTest: (
    organization_id: string,
    assistant_id: string,
    messages: SimulatedTurn[],
    execute_tools: boolean,
  ) =>
    invoke<{ turns: SimulatedTurn[]; ended: boolean; missing_variables: string[] }>(
      "voice-assistants-chat-test",
      { organization_id, assistant_id, action: "message", messages, execute_tools },
    ),

  // ── Calls ──
  /**
   * Backfill one bounded batch of the assistant's Retell calls. Pass the
   * returned `pagination_key` back to continue; loop until `has_more` is false.
   */
  syncCalls: (organization_id: string, assistant_id: string, pagination_key?: string | null) =>
    invoke<{ fetched: number; upserted: number; pagination_key: string | null; has_more: boolean }>(
      "voice-assistants-sync-calls",
      { organization_id, assistant_id, ...(pagination_key ? { pagination_key } : {}) },
    ),

  getRecordingUrl: (organization_id: string, id: string) =>
    invoke<{ recording_url: string | null }>("voice-assistants-get-recording-url", {
      organization_id,
      id,
    }),

  /** The provider's technical log for one call. Fetched on demand: its URL is signed for 24h. */
  getCallLog: (organization_id: string, id: string) =>
    invoke<{ log: string | null; reason?: "no_log" | "call_not_found" }>("voice-assistants-get-call-log", {
      organization_id,
      id,
    }),

  /**
   * The call's packet capture, as bytes. `invoke` parses JSON, and this is a
   * binary body, so the request goes out raw with the session token.
   */
  getCallPcap: async (organization_id: string, id: string): Promise<Blob> => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${EXTERNAL_SUPABASE_URL}/functions/v1/voice-assistants-get-call-pcap`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: EXTERNAL_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ organization_id, id }),
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error === "no_pcap" ? "Esta llamada no tiene captura." : msg?.error || `HTTP ${res.status}`);
    }
    return res.blob();
  },

  /** Transcript with the tool calls interleaved. On demand: it is 1,5x the stored transcript. */
  getCallTools: (organization_id: string, id: string) =>
    invoke<{
      turns: Array<Record<string, unknown>> | null;
      tool_calls?: Array<Record<string, unknown>>;
      reason?: "no_transcript" | "call_not_found";
    }>("voice-assistants-get-call-tools", { organization_id, id }),

  /** Motor de Voz total concurrency limit (caps the campaign's simultaneous calls). */
  getConcurrency: (organization_id: string) =>
    invoke<{ concurrency_limit: number; current_concurrency: number }>(
      "voice-assistants-get-concurrency",
      { organization_id },
    ),

  /** Full Retell config (agent + voice-engine LLM) as JSON, à la Retell export. */
  exportAssistant: (organization_id: string, id: string) =>
    invoke<{ export_data: Record<string, unknown>; file_name: string }>("voice-assistants-export-assistant", {
      organization_id,
      id,
    }),

  /** Pull this assistant's config from Retell (a specific version, or the live
   * latest) into its Flow row + the version's general_tools. */
  syncAssistant: (organization_id: string, id: string, version?: number) =>
    invoke<{ assistant: VoiceAssistant; remote: unknown; tools: Array<Record<string, unknown>> }>(
      "voice-assistants-sync-assistant",
      { organization_id, id, version },
    ),

  // ── External webhook (server-side test POST) ──
  testExternalWebhook: (organization_id: string, url: string, secret?: string) =>
    invoke<{ ok: boolean; status?: number; error?: string | null }>("voice-assistants-test-external-webhook", {
      organization_id,
      url,
      secret,
    }),

  /** Test a custom-function tool against its endpoint (server-side fetch). */
  testCustomFunction: (
    organization_id: string,
    tool: {
      url?: string;
      method?: string;
      headers?: Record<string, string>;
      query_params?: Record<string, string>;
      timeout_ms?: number;
      send_args_only?: boolean;
    },
    opts?: { dynamic_vars?: Record<string, string>; body_override?: unknown; args?: Record<string, unknown> },
  ) =>
    invoke<{
      ok: boolean;
      status?: number;
      headers?: Record<string, string>;
      body?: unknown;
      duration_ms?: number;
      final_url?: string;
      method?: string;
      error?: string;
    }>("voice-assistants-test-custom-function", { organization_id, tool, ...opts }),

  // ── Tools (Retell general_tools on the LLM) ──
  listTools: (organization_id: string, assistant_id: string, version?: number | null) =>
    invoke<{ tools: Array<Record<string, unknown>> }>("voice-assistants-manage-tools", {
      organization_id,
      assistant_id,
      action: "list",
      version: version ?? undefined,
    }),

  saveTools: (
    organization_id: string,
    assistant_id: string,
    tools: Array<Record<string, unknown>>,
    version?: number | null,
  ) =>
    invoke<{ ok: boolean }>("voice-assistants-manage-tools", {
      organization_id,
      assistant_id,
      action: "save",
      tools,
      version: version ?? undefined,
    }),

  // ── Import existing Retell agent ──
  syncAgents: (organization_id: string) =>
    invoke<{ total: number; added: number; updated: number; removed: number; unsupported: number }>(
      "voice-assistants-sync-agents",
      { organization_id },
    ),

  previewAgentImport: (organization_id: string, agent_id: string) =>
    invoke<{ preview: AgentImportPreview }>("voice-assistants-import-agent", {
      organization_id,
      agent_id,
      action: "preview",
    }),

  importAgent: (organization_id: string, agent_id: string) =>
    invoke<{ assistant: VoiceAssistant; warnings?: string[] }>("voice-assistants-import-agent", {
      organization_id,
      agent_id,
      action: "import",
    }),

  previewAgentImportFile: (organization_id: string, export_data: unknown) =>
    invoke<{ preview: AgentImportPreview }>("voice-assistants-import-agent", {
      organization_id,
      export_data,
      action: "preview",
    }),

  importAgentFile: (organization_id: string, export_data: unknown) =>
    invoke<{ assistant: VoiceAssistant; warnings?: string[] }>("voice-assistants-import-agent", {
      organization_id,
      export_data,
      action: "import",
    }),

  // ── Retell agent versions ──
  listAgentVersions: (organization_id: string, assistant_id: string) =>
    invoke<{ versions: RetellVersion[] }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "list",
    }),

  getAgentVersion: (organization_id: string, assistant_id: string, version: number) =>
    invoke<{ config: AgentVersionConfig; tools: Array<Record<string, unknown>> }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "get",
      version,
    }),

  deleteAgentVersion: (organization_id: string, assistant_id: string, version: number) =>
    invoke<{ ok: boolean }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "delete",
      version,
    }),

  /** Create a new DRAFT version copied from `base_version`. Returns the new version #. */
  createAgentVersion: (organization_id: string, assistant_id: string, base_version: number) =>
    invoke<{ version: number | null }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "create",
      version: base_version,
    }),

  /** Publish a draft version as a fixed/published version in Retell. */
  publishAgentVersion: (organization_id: string, assistant_id: string, version: number) =>
    invoke<{ ok: boolean; version: number }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "publish",
      version,
    }),

  /** Rename a version (draft or published) — sets Retell's version_title. */
  setAgentVersionTitle: (organization_id: string, assistant_id: string, version: number, title: string) =>
    invoke<{ ok: boolean }>("voice-assistants-agent-versions", {
      organization_id,
      assistant_id,
      action: "set_title",
      version,
      title,
    }),
};
